import React from "react";
import { Box, Typography, Stack, Link } from "@mui/material";
import {
  type Control,
  type UseFormSetValue,
  type UseFormWatch,
} from "react-hook-form";
import { FileUploadButton } from "@/features/realEstateReceptionBookFeed/components/FileUploadButton";
import { ButtonVariantOption } from "@/components/Button";
import { type FormData } from "./formSchema";
import { MAX_CSV_ROWS } from "./useCsvUpload";
import { CAutocompleteMultiFreeSoloTextField } from "@/components/TextField/CAutocompleteMultiFreeSoloTextField";
import { useFeatureFlags } from "@/configs/featureFlag";
import { CMapPlotRequestNameSelector } from "./CMapPlotRequestNameSelector";
import { type MapPlotRequest } from "../../types";

interface CsvUploadTabProps {
  control: Control<FormData>;
  setValue: UseFormSetValue<FormData>;
  watch: UseFormWatch<FormData>;
  fileToCsvUpload: File | null;
  showFileErrorMessage: boolean;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  existingRequests: MapPlotRequest[];
  existingTags: string[];
  isRequestsLoading: boolean;
}

export const CsvUploadTab: React.FC<CsvUploadTabProps> = ({
  control,
  setValue,
  watch,
  fileToCsvUpload,
  showFileErrorMessage,
  handleFileChange,
  existingRequests,
  existingTags,
  isRequestsLoading,
}) => {
  const { crm: crmFeatureFlag } = useFeatureFlags();
  const isCrmEnabled = Boolean(crmFeatureFlag);
  const sampleCsvPath = isCrmEnabled
    ? "/assets/crm/sample_地図プロットサンプル.csv"
    : "/assets/non_crm/sample_地図プロットサンプル.csv";
  const sampleExcelPath = isCrmEnabled
    ? "/assets/crm/sample_地図プロットサンプル.xlsx"
    : "/assets/non_crm/sample_地図プロットサンプル.xlsx";

  return (
    <Box>
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Link href={sampleCsvPath}>見本CSVをダウンロード</Link>
        <Link href={sampleExcelPath}>見本Excelをダウンロード</Link>
      </Stack>
      <Stack alignItems="center" sx={{ mt: 2 }}>
        <Box>
          <Typography variant="h6">
            CSVまたはエクセルをアップロードする
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            mt: 2,
            width: "100%",
            maxWidth: "600px",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
            <Box sx={{ flex: 1 }}>
              <CMapPlotRequestNameSelector
                control={control}
                setValue={setValue}
                watch={watch}
                existingRequests={existingRequests}
                required={true}
                isLoading={isRequestsLoading}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: "bold" }}
              >
                タグ
              </Typography>
              <CAutocompleteMultiFreeSoloTextField
                name="tags"
                control={control}
                label="タグを追加"
                maxLength={20}
                options={existingTags}
                isLoading={isRequestsLoading}
                tooltipText={
                  <>
                    区切り文字（スペース、カンマ、タブ、改行）で複数入力が可能です
                    <br />
                    コピー＆ペーストでも複数項目を一括で入力できます
                    <br />
                    既存の識別名を指定した場合、タグを入力すると上書きされます
                  </>
                }
              />
            </Box>
          </Box>
          <FileUploadButton
            variant={ButtonVariantOption.Contained}
            onChange={handleFileChange}
            accept={".csv, .xlsx, .xls"}
            label={"アップロード"}
          />
          {fileToCsvUpload && <Typography>{fileToCsvUpload.name}</Typography>}
          {showFileErrorMessage && (
            <Typography color="error">
              {`CSVファイルの行数は${MAX_CSV_ROWS.toLocaleString()}以下にしてください。`}
            </Typography>
          )}
        </Box>
      </Stack>

      <Box sx={{ mt: 3, width: "100%" }}>
        <Typography variant="caption" color="text.secondary">
          ※ 1つの物件リストに保存できる物件は最大で
          {MAX_CSV_ROWS.toLocaleString()}件です
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.5 }}
        >
          ※
          データの登録はバックグラウンドで処理されます。処理完了後にメールでお知らせが届きましたら、データを取得できます
        </Typography>
      </Box>
    </Box>
  );
};
